import React, { useEffect, useRef, useState } from 'react';

import { Textarea } from '@/components/ui/textarea';

interface VariableTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  value?: string;
  onValueChange?: (value: string) => void;
  variables?: string[];
  dropdownEnabled?: boolean;
}

export function VariableTextarea({
  value = '',
  onValueChange,
  onChange,
  className,
  variables = [],
  dropdownEnabled = false,
  ...props
}: VariableTextareaProps) {
  const [text, setText] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [triggerPosition, setTriggerPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  if (prevValue !== value) {
    setPrevValue(value);
    setText(value);
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Sync scroll between textarea and display div
  const handleScroll = () => {
    if (textareaRef.current && displayRef.current) {
      displayRef.current.scrollTop = textareaRef.current.scrollTop;
      displayRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const insertVariable = (variable: string) => {
    const before = text.slice(0, triggerPosition);
    const after = text.slice(triggerPosition + 2 + searchQuery.length);
    const newText = `${before}{{${variable}}}${after}`;

    setText(newText);
    onValueChange?.(newText);
    setShowDropdown(false);
    setSearchQuery('');

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handlePaste = () => {
    setTimeout(() => {
      if (textareaRef.current) {
        const newText = textareaRef.current.value;
        setText(newText);
        onValueChange?.(newText);
      }
    }, 0);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    // Forward the native event so Formik's field handler still runs, then emit
    // the parsed value so consumers can re-extract variables from the text.
    onChange?.(e);
    onValueChange?.(newText);

    if (dropdownEnabled) {
      // Check if user typed {{
      const cursorPosition = e.target.selectionStart;
      const textBeforeCursor = newText.slice(0, cursorPosition);
      const lastTwoBrackets = textBeforeCursor.lastIndexOf('{{');

      if (lastTwoBrackets !== -1) {
        const textAfterBrackets = textBeforeCursor.slice(lastTwoBrackets + 2);

        // Check if there's a closing }} between the opening {{ and cursor
        const textBetweenBracketsAndCursor = newText.slice(lastTwoBrackets, cursorPosition);
        const hasClosing = textBetweenBracketsAndCursor.includes('}}');

        if (!hasClosing) {
          setTriggerPosition(lastTwoBrackets);
          setSearchQuery(textAfterBrackets);
          setShowDropdown(true);

          // Calculate dropdown position at cursor
          if (textareaRef.current) {
            const textarea = textareaRef.current;
            const rect = textarea.getBoundingClientRect();

            // Get computed styles
            const styles = window.getComputedStyle(textarea);
            const lineHeight = parseFloat(styles.lineHeight) || 24;
            const paddingTop = parseFloat(styles.paddingTop) || 8;
            const paddingLeft = parseFloat(styles.paddingLeft) || 12;
            const fontSize = parseFloat(styles.fontSize) || 14;

            // Count lines before cursor
            const textUpToCursor = textBeforeCursor;
            const lines = textUpToCursor.split('\n');
            const currentLine = lines.length - 1;
            const currentLineText = lines[currentLine] || '';

            // Calculate character position in current line (rough estimate)
            const charWidth = fontSize * 0.6; // Approximate monospace width
            const charsBeforeVariable = currentLineText.length;

            // Calculate vertical position (accounting for scroll)
            const lineOffset = currentLine * lineHeight;
            const scrollTop = textarea.scrollTop || 0;
            const verticalPos = rect.top + paddingTop + lineOffset - scrollTop + lineHeight;

            // Calculate horizontal position
            const scrollLeft = textarea.scrollLeft || 0;
            const horizontalPos =
              rect.left + paddingLeft + charsBeforeVariable * charWidth - scrollLeft;

            // Determine if dropdown should appear above or below
            const dropdownHeight = 280; // Approximate height
            const spaceBelow = window.innerHeight - verticalPos;
            const spaceAbove = rect.top + paddingTop + lineOffset - scrollTop;
            const shouldShowAbove = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

            const finalTop = shouldShowAbove
              ? rect.top + paddingTop + lineOffset - scrollTop - dropdownHeight
              : verticalPos;

            setDropdownPosition({
              top: finalTop,
              left: Math.max(
                rect.left + paddingLeft,
                Math.min(horizontalPos, window.innerWidth - 350)
              ), // Keep within viewport
            });
          }
        } else {
          setShowDropdown(false);
        }
      } else {
        setShowDropdown(false);
      }
    }
  };

  const filteredVariables = variables.filter(v =>
    v.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Parse text and highlight variables
  const renderHighlightedText = () => {
    const regex = /\{\{([^}]+)\}\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before the variable
      if (match.index > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>);
      }

      // Add the variable with styling
      parts.push(
        <span
          key={`var-${match.index}`}
          className="font-medium py-px px-1 border rounded-lg text-primary bg-primary/10 dark:bg-primary/20 border-primary/30 dark:border-primary/40"
          style={{ display: 'inline-block', verticalAlign: 'baseline' }}
        >
          {`{{${match[1]}}}`}
        </span>
      );

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(<span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>);
    }

    return parts.length > 0 ? parts : <span>{text}</span>;
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        {/* Actual textarea - make transparent when not focused and has variables */}
        <Textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onPaste={handlePaste}
          onScroll={handleScroll}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          className={`${className} ${
            !isFocused && text.includes('{{') && text.includes('}}')
              ? 'text-transparent caret-foreground'
              : ''
          }`}
          style={{
            caretColor:
              !isFocused && text.includes('{{') && text.includes('}}') ? 'transparent' : undefined,
          }}
          {...props}
        />

        {/* Highlighted display layer - only show when not focused and has variables */}
        {!isFocused && text.includes('{{') && text.includes('}}') && (
          <div
            ref={displayRef}
            className="variable-textarea-overlay absolute inset-0 pointer-events-none whitespace-pre-wrap break-words overflow-auto rounded-md px-3 py-2 text-sm bg-transparent"
            style={{
              lineHeight: '1.5',
              scrollbarWidth: 'none', // Hide scrollbar for Firefox
              msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
            }}
            aria-hidden="true"
          >
            {renderHighlightedText()}
          </div>
        )}
      </div>

      {showDropdown && (
        <div
          ref={dropdownRef}
          className="fixed z-[100] bg-card border border-border rounded-lg shadow-lg w-[320px]"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className="p-2">
            <div className="px-3 py-2 border-b border-border bg-muted/50 rounded-t-md -m-2 mb-2">
              <p className="text-xs font-medium text-muted-foreground tracking-wide">
                System variables
              </p>
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              {filteredVariables.length === 0 ? (
                <div className="px-3 py-2.5 text-sm text-muted-foreground">No variables found</div>
              ) : (
                filteredVariables.map(variable => (
                  <button
                    key={variable}
                    className="w-full px-3 py-2.5 text-sm text-left hover:bg-primary/10 hover:text-primary rounded-md cursor-pointer transition-colors flex items-center gap-2.5"
                    onMouseDown={e => {
                      e.preventDefault();
                      insertVariable(variable);
                    }}
                    type="button"
                  >
                    <span className="text-sm">{variable}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
